import React, { createContext, useCallback, useContext, useState } from 'react';

import { Share as CapacitorShare } from '@capacitor/share';

import { useClinic } from './ClinicContext';
import { useDentist } from '@/contexts/DentistContext';
import { useTracker } from '@/contexts/TrackerContext';
import {
  Share,
  createShare,
  deleteShare,
  getSharesByClinicId,
  getSharesByDentistId,
  updateShareCode
} from '@/data/Shares';
import { useToast } from '@/hooks';
import { encodeShareCode } from '@/utils/helpers/encodeShareCode';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { EVENTS } from '@/utils/tracker';
import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';

interface ShareContextType {
  shareLink: string;
  share: Share | null;
  onUpdateShareCode: (code: string) => Promise<void>;
  onShare: () => Promise<void>;
  loading: boolean;
  loaded: boolean;
}

const ShareContext = createContext<ShareContextType | undefined>(undefined);

export const ShareProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const displayToast = useToast();
  const { selectedClinic: clinic } = useClinic();
  const { selectedDentist: dentist } = useDentist();
  const { captureEvent } = useTracker();
  const queryClient = useQueryClient();

  const [shareLink, setShareLink] = useState<string>('');

  const {
    data,
    isLoading: loadingData,
    isFetched: loaded
  }: UseQueryResult<{ shares: Share[]; share: Share | null }, Error> = useQuery(
    {
      queryKey: ['shares', clinic?.id, dentist?.id],
      queryFn: async () => {
        if (!dentist?.id) return { shares: [] satisfies Share[], share: null };

        try {
          const fetchedShares: Share[] = await getSharesByDentistId(
            dentist.id!
          );

          if (fetchedShares.length > 0) {
            const latestShare = fetchedShares[fetchedShares.length - 1];
            setShareLink(`https://www.odonto.ph/${latestShare.code}`);
            return { shares: fetchedShares, share: latestShare };
          }

          if (clinic?.id) {
            const fetchedShares: Share[] = await getSharesByClinicId(
              clinic.id!
            );

            if (fetchedShares.length > 0) {
              const latestShare = fetchedShares[fetchedShares.length - 1];
              setShareLink(`https://www.odonto.ph/${latestShare.code}`);
              return { shares: fetchedShares, share: latestShare };
            }
            setShareLink(`https://www.odonto.ph/schedule/${clinic.id}`);
          }

          return { shares: [] satisfies Share[], share: null };
        } catch {
          displayToast({
            message: 'Please check your internet connection.',
            duration: 3000,
            position: 'bottom',
            positionAnchor: 'tab-bar'
          });
          return { shares: [] satisfies Share[], share: null };
        }
      },
      enabled: !!dentist?.id
    }
  );

  const shares = data?.shares || [];
  const share = data?.share || null;

  const deleteShareMutation = useMutation({
    mutationFn: async (code: string) => {
      await deleteShare(code);
    }
  });

  const createShareMutation = useMutation({
    mutationFn: async (newShare: Share) => {
      const createdShare = await queryClient.fetchQuery({
        queryKey: ['createShare', newShare.dentist_id, newShare.clinic_id],
        queryFn: () => createShare(newShare)
      });
      if (!createdShare) {
        throw new Error('Failed to create share');
      }
      return createdShare;
    },
    onSuccess: (newShare) => {
      queryClient.setQueryData(
        ['shares', clinic?.id, dentist?.id],
        (old: any) => ({
          shares: [...(old?.shares || []), newShare],
          share: newShare
        })
      );
      setShareLink(`https://www.odonto.ph/${newShare.code}`);
    },
    onError: () => {
      displayToast({
        message:
          'The booking link you entered is already in use. Please enter a different link.',
        duration: 2400,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  });

  const updateShareCodeMutation = useMutation({
    mutationFn: async (code: string) => {
      if (!share?.code) throw new Error('No share exists to update');
      return await queryClient.fetchQuery({
        queryKey: ['updateShareCode', share.code, code],
        queryFn: () => updateShareCode(share.code, code)
      });
    },
    onSuccess: (updatedShare) => {
      if (!updatedShare) return;

      queryClient.setQueryData(
        ['shares', clinic?.id, dentist?.id],
        (old: any) => ({
          shares: (old?.shares || []).map((s: Share) =>
            s.code === share?.code ? updatedShare : s
          ),
          share: updatedShare
        })
      );
      setShareLink(`https://www.odonto.ph/${updatedShare.code}`);
      displayToast({
        message: 'Your custom link has been updated.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    },
    onError: () => {
      displayToast({
        message: 'Failed to update custom link. Please try again.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  });

  const onUpdateShareCode = useCallback(
    async (code: string) => {
      const encodedCode = encodeShareCode(code);
      if (!code) {
        displayToast({
          message: 'Custom link cannot be empty.',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
        return;
      }

      if (share && share.code === encodedCode) {
        displayToast({
          message:
            'The custom link you entered matched your previous one. Please enter a different link.',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
        return;
      }

      if (share) {
        await updateShareCodeMutation.mutateAsync(encodedCode);
      } else {
        const duplicateShares = shares.filter(
          (existingShare: Share) => existingShare.code === encodedCode
        );
        if (duplicateShares.length > 0) {
          await Promise.all(
            duplicateShares.map((existingShare: Share) =>
              deleteShareMutation.mutateAsync(existingShare.code)
            )
          );
        }

        await createShareMutation.mutateAsync({
          code: encodedCode,
          clinic_id: clinic?.id!,
          dentist_id: dentist?.id
        } satisfies Share);
      }
    },
    [
      shares,
      share,
      clinic?.id,
      dentist?.id,
      deleteShareMutation,
      createShareMutation,
      updateShareCodeMutation
    ]
  );

  const onShare = useCallback(async () => {
    try {
      await CapacitorShare.share({
        text: `Schedule your appointment with ${formatDentistName({
          dentist,
          returnDentistFullName: true
        })}: ${shareLink}`,
        dialogTitle: 'Share your booking link to add new appointments'
      });
      captureEvent({
        event: EVENTS.CODE_SHARED,
        properties: { share_link: shareLink }
      });
    } catch (error: any) {
      if (!error.message.includes('cancel')) {
        displayToast({
          message: `${error.message}`,
          duration: 2400,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      }
    }
  }, [dentist, shareLink, captureEvent]);

  const loading =
    loadingData ||
    createShareMutation.isPending ||
    deleteShareMutation.isPending ||
    updateShareCodeMutation.isPending;

  return (
    <ShareContext.Provider
      value={{ shareLink, share, onUpdateShareCode, onShare, loading, loaded }}
    >
      {children}
    </ShareContext.Provider>
  );
};

export const useShare = () => {
  const context = useContext(ShareContext);
  if (!context) {
    throw new Error('useShare must be used within a ShareProvider');
  }
  return context;
};
